import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { products, categoryMap, categoryOrder } from '@/mocks/products';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab') || 'all';
    setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const filteredProducts = activeTab === 'all'
    ? products
    : products.filter((p) => p.category === activeTab);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams(tab === 'all' ? {} : { tab });
  };

  const tabs = [
    { key: 'all', label: '全部产品' },
    ...categoryOrder.map((key) => ({
      key,
      label: categoryMap[key],
    })),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '黄油小熊周边产品分类',
    description: '浏览黄油小熊全部周边产品，包括毛绒玩偶、精致配饰、居家好物、文具手账、联名限定及收藏臻品。',
    url: 'https://butterbear.com/category',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="pt-20 pb-14 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: activeTab === 'all' ? '全部产品' : categoryMap[activeTab] || activeTab },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              探索小熊周边
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              找到属于你的那一份温暖与治愈
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无产品，敬请期待~</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.02] border border-background-200/70"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - 黄油小熊${categoryMap[product.category]}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      {product.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-foreground-400 mb-1 block">
                      {categoryMap[product.category]}
                    </span>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-sm font-semibold text-primary-600">{product.price}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
}